import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useEnvironment } from "@/common/contexts/EnvironmentContext";
import api from "@/common/utils/api";
import { integrationQueryKeys } from "@/integration/data/integrationQueryKeys";
import {
  LinearArchiveGuideOptions,
  LinearIssueTemplate,
} from "@reflag/shared/linearConnectionAPI";

export function useLinearTemplateActions() {
  const { org } = useEnvironment();
  const queryClient = useQueryClient();
  const cacheTemplate = (template: LinearIssueTemplate) => {
    queryClient.setQueryData<LinearArchiveGuideOptions>(
      integrationQueryKeys.linearArchiveGuideOptions(org.id),
      (previous) =>
        previous
          ? {
              ...previous,
              templates: [
                ...previous.templates.filter((item) => item.id !== template.id),
                template,
              ],
            }
          : previous,
    );
  };
  const create = useMutation({
    mutationFn: () =>
      api
        .post<"/orgs/:orgId/linear/connection/issue-templates">(
          `/orgs/${org.id}/linear/connection/issue-templates`,
          {},
        )
        .then((response) => response.data),
    retry: false,
    onSuccess: (result) => cacheTemplate(result.template),
  });
  const inspect = useMutation({
    mutationFn: (id: string) =>
      api
        .get<"/orgs/:orgId/linear/connection/issue-templates/:templateId">(
          `/orgs/${org.id}/linear/connection/issue-templates/${id}`,
        )
        .then((response) => response.data),
    onSuccess: cacheTemplate,
  });
  const setDelegate = useMutation({
    mutationFn: ({ id, delegateId }: { id: string; delegateId: string }) =>
      api
        .patch<"/orgs/:orgId/linear/connection/issue-templates/:templateId">(
          `/orgs/${org.id}/linear/connection/issue-templates/${id}`,
          { delegateId },
        )
        .then((response) => response.data),
    retry: false,
    onSuccess: cacheTemplate,
  });
  return { create, inspect, setDelegate };
}
